import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import { useFormCrearCiclo } from "../../hooks/useFormCrearCiclo";
import { bogotaNow, normalizePlate } from "../../model/cycleModel";
import ConfirmacionCreacionDialog from "./ConfirmacionCreacionDialog";

export default function FormCrearCiclo({ onSubmit, initialData, loading: externalLoading }) {
  const form = useFormCrearCiclo(onSubmit, initialData);
  const [confirmation, setConfirmation] = useState(null);
  const [created, setCreated] = useState(false);
  const busy = form.isSubmitting || externalLoading;
  const selected = form.commerces.find((item) => item.id === form.values.commerce) || null;
  const prepare = async (event) => {
    event.preventDefault();
    const errors = await form.validateForm();
    form.setTouched({ commerce: true, plate: true, date: true, time: true });
    if (Object.keys(errors).length || !selected || busy) return;
    form.setSubmitError(null);
    setCreated(false);
    setConfirmation({ ...form.values, commerceName: selected.name, commerceCode: selected.code });
  };
  const confirm = async () => {
    const result = await form.executeSubmit();
    if (result?.success) setCreated(true);
    else setConfirmation(null);
  };
  const close = () => {
    if (!busy) {
      setConfirmation(null);
      setCreated(false);
    }
  };
  const field = (name, label, type = "text") => (
    <TextField
      fullWidth
      size="medium"
      name={name}
      label={label}
      type={type}
      required
      value={form.values[name]}
      onBlur={form.handleBlur}
      disabled={busy}
      onChange={
        name === "plate"
          ? (event) =>
              form.setFieldValue(
                name,
                normalizePlate(event.target.value)
                  .replace(/[^A-Z0-9]/g, "")
                  .slice(0, 6)
              )
          : form.handleChange
      }
      error={Boolean(form.touched[name] && form.errors[name])}
      helperText={form.touched[name] && form.errors[name]}
      InputLabelProps={type !== "text" ? { shrink: true } : undefined}
      inputProps={
        name === "date" ? { max: bogotaNow().date } : name === "plate" ? { maxLength: 8 } : {}
      }
    />
  );
  return (
    <Box
      component="form"
      onSubmit={prepare}
      sx={{
        "& .MuiOutlinedInput-root": {
          minHeight: 56,
          borderRadius: 2,
          bgcolor: "#fff",
          "& .MuiOutlinedInput-notchedOutline": { borderColor: "#91a6b8", borderWidth: "1.5px" },
          "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#185a75" },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: "#185a75",
            borderWidth: 2,
          },
          "&.Mui-error .MuiOutlinedInput-notchedOutline": { borderColor: "#c03945" },
          "&.Mui-disabled": { bgcolor: "#eef3f7" },
        },
        "& .MuiInputBase-input": { fontSize: "1rem", fontWeight: 500, color: "#183b50" },
        "& .MuiInputLabel-root": {
          fontSize: "1rem",
          fontWeight: 600,
          color: "#405972",
          bgcolor: "#fff",
          px: 0.5,
        },
        "& .MuiInputLabel-root.Mui-focused": { color: "#185a75" },
        "& .MuiInputLabel-root.Mui-error": { color: "#c03945" },
      }}
    >
      <Typography sx={{ fontWeight: 700, fontSize: "1rem", color: "#183b50" }}>
        Crear ingreso
      </Typography>
      <Typography sx={{ fontSize: ".8rem", color: "#526579", mb: 2 }}>
        Seleccione el comercio, la placa y la fecha de ingreso. Horario de Bogotá.
      </Typography>
      {form.errorCommerces && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            <Button onClick={form.reloadCommerces} disabled={form.loadingCommerces}>
              Reintentar
            </Button>
          }
        >
          {form.errorCommerces}
        </Alert>
      )}
      {form.submitError && (
        <Alert severity="warning" sx={{ mb: 2 }} onClose={() => form.setSubmitError(null)}>
          {form.submitError}
        </Alert>
      )}
      <Grid container spacing={2}>
        <Grid item xs={12} md={5}>
          <Autocomplete
            options={form.commerces}
            value={selected}
            size="medium"
            loading={form.loadingCommerces}
            disabled={busy || form.loadingCommerces}
            getOptionLabel={(item) => `${item.name} · ${item.code}`}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            onChange={(_, item) => form.setFieldValue("commerce", item?.id || "")}
            noOptionsText="No se encontraron comercios"
            loadingText="Cargando comercios..."
            renderInput={(params) => (
              <TextField
                {...params}
                label="Comercio"
                required
                error={Boolean(form.touched.commerce && form.errors.commerce)}
                helperText={form.touched.commerce && form.errors.commerce}
              />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={4} md={2}>
          {field("plate", "Placa")}
        </Grid>
        <Grid item xs={12} sm={4} md={3}>
          {field("date", "Fecha de ingreso", "date")}
        </Grid>
        <Grid item xs={12} sm={4} md={2}>
          {field("time", "Hora de ingreso", "time")}
        </Grid>
      </Grid>
      <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
        <Button
          type="submit"
          variant="contained"
          disabled={busy || form.loadingCommerces || !form.commerces.length}
          sx={{ bgcolor: "#185a75", color: "#fff", "&:hover": { bgcolor: "#103f54" } }}
        >
          {busy ? <CircularProgress size={18} color="inherit" sx={{ mr: 1 }} /> : null}Revisar y
          crear
        </Button>
      </Box>
      <ConfirmacionCreacionDialog
        open={Boolean(confirmation)}
        onClose={close}
        onConfirm={created ? close : confirm}
        formData={confirmation}
        cicloCreado={created}
        loading={busy}
      />
    </Box>
  );
}
FormCrearCiclo.propTypes = {
  onSubmit: PropTypes.func,
  initialData: PropTypes.object,
  loading: PropTypes.bool,
};
FormCrearCiclo.defaultProps = { onSubmit: null, initialData: undefined, loading: false };
